import type { MouseEventHandler } from "react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { Turnstile } from "@marsidev/react-turnstile";
import { captureException, ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";

import { blackOmgyes } from "@components/common/omgyesLogo";
import {
  EMAIL_VALIDATION_PATTERN,
  emailValidationRules,
  Form,
  FormInput,
} from "@components/forms";
import { Button } from "@components/ui/Button";

import { deviceContext } from "@lib/analytics/deviceContext";
import { SEGMENT_USER_TIMEOUT_MS } from "@lib/analytics/segmentTimeout";
import { isUpgradeOption, type PurchaseOption } from "@lib/api/checkout.types";
import { Auth } from "@lib/authentication/amplify/amplify";
import { generateDummyPassword } from "@lib/authentication/generateDummyPassword";
import { probeCheckoutApi } from "@lib/checkout/apiReachabilityProbe";
import { resolveCheckoutOption } from "@lib/checkout/checkoutPrefetch";
import { getDiscountQueryOptions } from "@lib/helpers/getDiscountQueryOptions";
import { getProductPricingQueryOptions } from "@lib/helpers/getProductPricingQueryOptions";
import useAnalytics from "@lib/hooks/useAnalytics";
import { TimeoutError, withTimeout } from "@lib/withTimeout";
import { formatPrice } from "@pages/utils.ts";
import { AnalyticsEvent } from "@root/constants";
import {
  clearCheckoutAttempt,
  clearCheckoutBuyerIdentity,
  setCheckoutBuyerEmail,
} from "@utils/sentry";

import { getBraintreeChunk } from "./braintreeChunk";
import CheckoutFooter from "./CheckoutFooter";
import { CHECKOUT_INPUT_CLASS, InputErrorLabel } from "./checkoutForm";
import GiftConfirmation from "./GiftConfirmation";
import { VariantCardEmail } from "./VariantCardEmail";
import { VariantSetPassword } from "./VariantSetPassword";

// BraintreeCheckout is lazy() so the ~129 KiB gzipped drop-in chunk —
// plus the ~390 KiB PayPal `pay.js` SDK and other transitive payment
// SDKs it loads — stays out of the LCP-critical entry bundle. The
// DOWNLOAD, however, no longer waits for this component to render:
// checkoutPrefetch.ts kicks off the shared import promise
// (braintreeChunk.ts) at page entry, parallel with the pricing request,
// and the lazy() factory below consumes the same memoized promise — one
// fetch, started early (U2/R1, plan 2026-07-07-004).
//
// The lazy component is held in component STATE, not module scope:
// React 18 caches a rejected lazy factory result permanently, so
// recovering from a failed chunk load requires recreating the lazy
// component with a fresh import (the chunk memo resets on rejection).
//
// The Suspense fallback is intentionally `null`: this renders inside a
// `hidden` wrapper (or behind the pricing gate), so a brief unresolved
// state is invisible. A Sentry ErrorBoundary below catches hard chunk
// failures (CDN blip, deploy-rotated hash) so we surface a recoverable
// UI instead of unmounting the whole tree.

// Tag every checkout event with the source so A/B analysis and funnel
// queries can distinguish the React checkout from the legacy doSignup flow
// without relying on URL matching.
const CHECKOUT_SOURCE = { checkout_source: "react_checkout" } as const;

// OMG-2134: how long a required (invisible) Turnstile may go without minting a
// token before we treat it as hung and fire human_verification_hung. Generous
// vs the ~1-2s a healthy widget takes, so this flags real hangs, not slow
// networks.
const TURNSTILE_HUNG_MS = 10_000;

// OMG-2122: last-resort backstop for a Turnstile script that is blocked so
// silently it fires nothing at all — not a widget callback AND not the script
// tag's own onerror (some content blockers cancel the request without an error
// event). The precise, fast path for a blocked/dead script is now
// `scriptOptions.onError` (the <script> onerror, see the widget below); this
// timer only covers the residual "no event whatsoever" case. It is deliberately
// long: a merely-slow script must render (onWidgetLoad) before it fires, so a
// slow VPN load never trips the hint next to a fresh challenge. onWidgetLoad
// also retracts the hint if a late render beats a false positive here.
const TURNSTILE_LOAD_TIMEOUT_MS = 20000;

// Pricing-failure recovery (R17/R6, plan 2026-07-07-004). One component for
// every arm: control renders it inside the Step-1 form; the payment-first
// variant and gift — whose whole screen sits behind the pricing gate — render
// it where the payment section would be, instead of a permanently blank page.
// Focus moves to the alert on mount (screen-reader/keyboard parity with the
// Turnstile-fallback focus pattern). Double-clicks are safe: react-query
// dedupes concurrent refetches of the same query.
// Focus the error alert on mount UNLESS the user is mid-typing in a text
// field — on the control arm the Step-1 email/password form renders alongside
// the pricing error, and a slow pricing request erroring mid-keystroke must
// not yank focus off the input (julik review finding, PR #849).
const focusUnlessTyping = (el: HTMLElement | null) => {
  const active = document.activeElement;
  if (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    // Braintree hosted fields are cross-origin iframes — when the buyer is
    // typing a card number, activeElement is the <iframe>, not an input.
    // Without this, the error alert would yank them out of the card-number
    // field mid-entry (review: adversarial + julik).
    active instanceof HTMLIFrameElement
  ) {
    return;
  }
  el?.focus();
};

const PricingLoadError = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  const alertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    focusUnlessTyping(alertRef.current);
  }, []);
  return (
    <div
      ref={alertRef}
      tabIndex={-1}
      className="mt-[20px] flex flex-col items-center gap-[12px] text-center outline-none"
      id="checkout-pricing-error"
      role="alert"
    >
      <p className="font-mabry-pro-medium text-[16px] text-ui-pepper">
        {t("js.checkout.pricingLoadFailed", {
          defaultValue:
            "We couldn't load pricing for this product. Check your connection and try again.",
        })}
      </p>
      <Button size="full" className="max-w-[200px]" onClick={onRetry}>
        {t("label.tryAgain", {
          defaultValue: "Try again",
        })}
      </Button>
    </div>
  );
};

// Chunk-load failure recovery (U3/R6). "Try again" re-imports without a full
// page reload — it recreates the state-held lazy component (React caches a
// lazy rejection permanently) against the auto-reset chunk memo, then clears
// the boundary. "Reload page" stays as the escape hatch for the case a
// re-import can never fix: a deploy-mid-session rotated the chunk hash out
// of the cached index.html.
const ChunkLoadError = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  const alertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    focusUnlessTyping(alertRef.current);
  }, []);
  return (
    <div
      ref={alertRef}
      tabIndex={-1}
      role="alert"
      id="checkout-chunk-error"
      className="mt-[20px] flex flex-col items-center gap-[12px] text-center px-[20px] outline-none"
    >
      <p className="font-mabry-pro-medium text-[16px] text-ui-pepper">
        {t("js.checkout.paymentLoadFailed", {
          defaultValue:
            "We couldn't load the payment form. Check your connection and reload to try again.",
        })}
      </p>
      <Button size="full" className="max-w-[200px]" onClick={onRetry}>
        {t("label.tryAgain", {
          defaultValue: "Try again",
        })}
      </Button>
      <Button
        size="full"
        className="max-w-[200px]"
        onClick={() => window.location.reload()}
      >
        {t("label.reload", {
          defaultValue: "Reload page",
        })}
      </Button>
    </div>
  );
};

// Static FALLBACK conversion table — only consulted when the pricing row
// carries no maintained USD price (see convertRevenueUsd below). Rates as of
// 2026-08-14 (open.er-api.com). Hardcoded rates rot: the legacy table's ARS
// entry (0.0058, a ~2022 rate) was 8.7x stale by Aug 2026 and reported a
// 42,000 ARS purchase as $243.60 on the Convert revenue goal (OMG-2392).
// Returns null for unsupported currencies; caller falls back to the original
// amount.
const USD_CONVERSION_FACTORS: Record<string, number> = {
  ARS: 0.00067,
  AUD: 0.71,
  BRL: 0.19,
  CAD: 0.72,
  CHF: 1.23,
  CLP: 0.0011,
  CNY: 0.15,
  CZK: 0.048,
  DKK: 0.15,
  EUR: 1.15,
  GBP: 1.35,
  HKD: 0.13,
  HUF: 0.0032,
  ILS: 0.34,
  INR: 0.0105,
  JPY: 0.0063,
  KRW: 0.0007,
  MXN: 0.059,
  NOK: 0.105,
  NZD: 0.59,
  SEK: 0.105,
  SGD: 0.78,
  TWD: 0.031,
  USD: 1,
  ZAR: 0.062,
};

// USD-equivalent revenue for the convert_purchase_usd event + Convert.com
// pushRevenue. Preference order:
// 1. The pricing row's own maintained USD price (paypalPricing) — the pricing
//    seed keeps it current for exactly the high-inflation currencies (ARS,
//    CLP, KRW, ...) where a static FX rate rots fastest. Scaled by
//    price/list-price so percent discounts carry through.
// 2. The static table above (stable currencies drift slowly).
// 3. null — caller reports the original amount/currency unconverted.
// Exported for direct unit tests; the component path is also covered in
// checkout.test.tsx.
export function convertRevenueUsd(
  currency: string,
  price: number,
  pricing?: {
    amount: number;
    paypalPricing?: { amount: number; currency: "USD" } | null;
  },
): number | null {
  if (currency === "USD") return price;
  const usdList = pricing?.paypalPricing;
  if (
    usdList?.currency === "USD" &&
    usdList.amount > 0 &&
    pricing &&
    pricing.amount > 0
  ) {
    // Round to cents — the discount-ratio math can produce long floats.
    return Math.round(usdList.amount * (price / pricing.amount) * 100) / 100;
  }
  const factor = USD_CONVERSION_FACTORS[currency];
  return factor === undefined ? null : price * factor;
}

const lookupKeyByProduct = (productId: PurchaseOption, isGift?: boolean) => {
  if (isGift) {
    return `checkoutPage.charge.gift.${productId === "series" ? "essentials" : productId}`;
  }
  if (productId === "series") {
    return "checkoutPage.charge.collection";
  }

  if (productId === "series2,series3-upgrade") {
    return "checkout.charge.explore.upgrade";
  }

  return `checkoutPage.charge.${productId}.collection.explore`;
};

export interface GiftFields {
  isGift: true;
  giftDeliveryType: "send" | "print";
  giftRecipientEmail?: string;
  giftMessage?: string;
  giftDeliveryDate?: string;
  senderName?: string;
  // Contentful URL of the purchaser's selected card design. Emitted on
  // gift_purchased + gift_received so Iterable templates can display it.
  giftCardImage?: string;
}

const Checkout = ({ paymentFirst = false }: { paymentFirst?: boolean }) => {
  // State-held lazy component (see the lazy-loading comment block at the top
  // of this file): recreating it with a fresh lazy(getBraintreeChunk) is the
  // only way to recover after a rejected chunk import, since React caches a
  // lazy rejection permanently. The chunk memo itself lives in
  // braintreeChunk.ts and is shared with the pre-mount preload.
  const [BraintreeCheckout, setBraintreeCheckout] = useState(() =>
    lazy(getBraintreeChunk),
  );
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isGift = searchParams.get("isGift") === "true";
  // Payment-first variant (U2). Gift keeps the control two-step flow — its
  // recipient/confirmation email lives in Step 1 — so the reorder is scoped to
  // non-gift checkouts.
  const paymentFirstActive = paymentFirst && !isGift;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticatedEmail, setAuthenticatedEmail] = useState<string>();
  // False until the async auth check below settles. The payment-first screen
  // gates on it so it doesn't commit to a new-user-vs-authenticated layout
  // before `isAuthenticated` is known — otherwise an authenticated/upgrade
  // buyer sees the new-user "create account" UI for one paint before it flips
  // (OMG-2145).
  const [authResolved, setAuthResolved] = useState(false);
  const upgradeEmail = searchParams.get("email");
  const isUpgrade =
    searchParams.get("isUpgrade") === "true" &&
    (isAuthenticated || !!upgradeEmail);
  const [turnstileToken, setTurnstileToken] = useState<string>();
  const turnstileRef = useRef<TurnstileInstance>(null);
  // The slot is only mounted after a payment method is engaged. Starting a
  // timer at checkout/auth resolution counts time spent filling the account
  // form or choosing a method as a verification failure.
  const [turnstileContainer, setTurnstileContainer] =
    useState<HTMLDivElement | null>(null);
  const [turnstileInteracting, setTurnstileInteracting] = useState(false);
  // OMG-2122: many Turnstile errors are transient, so the first onError silently
  // resets the widget for one more attempt. Only a second consecutive failure
  // surfaces the actionable message below (VPN/connection hint) — a distrusted
  // IP that can't clear the interaction-only challenge has no other signal.
  const turnstileRetriedRef = useRef(false);
  // Flips true once the widget renders (onWidgetLoad) or fires any callback,
  // i.e. the script is alive. Read by the last-resort load-timeout backstop
  // below to distinguish "never loaded at all" from "loaded (maybe slowly)".
  const turnstileRespondedRef = useRef(false);
  const [turnstileBlocked, setTurnstileBlocked] = useState(false);
  // Why the current block was set, so onWidgetLoad can retract ONLY a
  // timeout-sourced false positive and never a genuine challenge/script
  // failure. This makes the retraction independent of the library's
  // onWidgetLoad-fires-once contract: even if a future upgrade re-fired it,
  // a real 'challenge' block can't be silently erased into a gated Buy Now.
  const turnstileBlockedSourceRef = useRef<
    "timeout" | "script_error" | "challenge" | "unsupported" | null
  >(null);
  const trackTurnstileMount = useCallback(
    (container: HTMLDivElement | null) => {
      setTurnstileContainer(container);
      if (container) {
        // The script may already exist, but this new widget still needs its
        // own response. A previous mount must not suppress its backstop.
        turnstileRespondedRef.current = false;
      } else {
        setTurnstileInteracting(false);
        if (turnstileBlockedSourceRef.current === "timeout") {
          turnstileBlockedSourceRef.current = null;
          setTurnstileBlocked(false);
        }
      }
    },
    [],
  );
  // Turnstile only gates logged-out checkout — the backend's verifyTurnstile
  // skips authenticated buyers entirely. So authed buyers (e.g. upgrades) get
  // no widget at all: rendering it would pop a pointless interaction-only
  // challenge for a distrusted-IP authed buyer whose token is never checked.
  // Gate on authResolved too (OMG-2145/OMG-2169): isAuthenticated starts false,
  // so without it an authed buyer would transiently mount the widget + get a
  // gated Buy Now during the auth-resolution window. Feeds both the widget
  // render and the BraintreeCheckout `turnstileRequired` prop (one source).
  const turnstileRequired = authResolved && !isAuthenticated;
  // OMG-2122: widget-mount last-resort backstop (see TURNSTILE_LOAD_TIMEOUT_MS).
  // scriptOptions.onError is the primary, precise signal for a blocked/dead
  // script; this only fires if NOTHING happened — no render, no onerror — after
  // the (long) timeout, so the buyer is never silently stranded behind a gated
  // Buy Now. onWidgetLoad flips turnstileRespondedRef the moment the widget
  // renders, so a slow-but-alive script no-ops this.
  useEffect(() => {
    if (!turnstileRequired || !turnstileContainer) return;
    const timer = setTimeout(() => {
      if (!turnstileRespondedRef.current) {
        turnstileBlockedSourceRef.current = "timeout";
        setTurnstileBlocked(true);
      }
    }, TURNSTILE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [turnstileRequired, turnstileContainer]);
  const [formMode, setFormMode] = useState<string>("new");
  const [formData, setFormData] = useState<Record<string, string>>();
  const [errors, setErrors] = useState<Record<string, any>>({});
  // Why step 1 is showing again after an automatic mode switch, rendered as a
  // notice above the form. "emailNotRecognized": the BE rejected an "existing
  // account" checkout with EMAIL_NOT_RECOGNIZED and we bounced the buyer back
  // in create-account mode (OMG-2129). "accountExists": the reverse, a
  // create-account checkout rejected with CUSTOMER_EXISTS, bounced back in
  // existing-account mode (OMG-2693). One value, so the two can never show
  // at once.
  const [modeSwitchNotice, setModeSwitchNotice] = useState<
    "emailNotRecognized" | "accountExists" | null
  >(null);
  const [signUpComplete, setSignUpComplete] = useState<boolean>(false);
  // Overrides the displayed price when the user selects PayPal in a
  // currency PayPal cannot settle in. BraintreeCheckout toggles this via
  // onActivePricingChange.
  const [activePricing, setActivePricing] = useState<{
    amount: number;
    currency: string;
  } | null>(null);

  const beginCheckoutFiredRef = useRef(false);
  // Idempotency guard for the success handler — the purchase events + Convert
  // revenue push must fire at most once. BraintreeCheckout's in-flight guard
  // already makes a double onSuccess unlikely; this is defense-in-depth so
  // revenue/conversions can't inflate if it's ever called twice.
  const purchaseTrackedRef = useRef(false);

  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const { register, handleSubmit, setValue, watch, getValues } = useForm();

  // On the variant the email lives on the single payment screen with no Step-1
  // submit to snapshot it into `formData`, so the charge reads it live. A new
  // variant user must supply a valid email here; authenticated/upgrade users
  // already have one via `authenticatedEmail`/`upgradeEmail`.
  const variantNewUser = paymentFirstActive && !isUpgrade && !isAuthenticated;
  // Payment-first creates the account at charge time with a generated throwaway
  // credential. Generate it once per checkout and hold it stably so the same
  // value is sent in the checkout request, reused for the post-charge sign-in,
  // and handed to the set-password screen (U5). In-memory only — never
  // persisted to URL, storage, or history.
  const [variantDummyPassword] = useState(generateDummyPassword);
  // Post-charge account setup for the variant: once set, the inline
  // set-password (or recovery) screen renders instead of the checkout form.
  // `email` is the resolved charge email reported by BraintreeCheckout — for
  // wallet/fallback buyers it lives only in the authorization payload, which
  // this component never sees, so U5 pre-fills from here rather than from the
  // (empty) card-step form value.
  const [accountSetup, setAccountSetup] = useState<{
    signInFailed: boolean;
    email: string;
  } | null>(null);
  const watchedEmail = watch("email");
  const variantEmailValid =
    !!watchedEmail && EMAIL_VALIDATION_PATTERN.test(watchedEmail);
  const variantEmailError = !!watchedEmail && !variantEmailValid;

  // OMG-2134: once a logged-out buyer has typed a valid email, set it as the
  // Sentry user so a CX-reported email resolves to this checkout session's
  // events + 100%-sampled replay. Authenticated buyers already carry a richer
  // identity (id + email) from NmhContext, so we leave theirs untouched. The
  // wallet-only path (no typed email) is covered at submit in BraintreeCheckout
  // from the authorization payload. Whether an anon identity was set — here or
  // on the wallet path — is tracked in sentry.ts so cleanup clears it either
  // way; authenticating flips setSentryUser's flag off so we never clobber it.
  useEffect(() => {
    if (authenticatedEmail) return; // NmhContext owns the identity; leave it.
    if (!watchedEmail || !EMAIL_VALIDATION_PATTERN.test(watchedEmail)) return;
    setCheckoutBuyerEmail(watchedEmail);
  }, [watchedEmail, authenticatedEmail]);
  // On leaving checkout: drop the checkout_attempt tag, and clear ONLY an
  // anonymous identity we set (never the authenticated identity NmhContext
  // owns) so neither rides along on later low-sampled sessions.
  useEffect(() => {
    return () => {
      clearCheckoutAttempt();
      clearCheckoutBuyerIdentity();
    };
  }, []);
  // KTD8 — the multi-step payment step charges on a single, pre-charge-
  // UNCONFIRMED email. The confirm-email match moved to the post-charge
  // Create-account screen (U5), so the pre-charge gate here is FORMAT-only.
  // The card buyer types this email on the card step (U2); a wallet buyer's
  // email arrives in the authorization payload (U3), so `watchedEmail` stays
  // empty for wallets and BraintreeCheckout resolves the charge email (and
  // creates the account) from the payload email + the dummy password.

  // Which drop-in option is active ("card" | "paypal" | "applePay" |
  // "googlePay" | "venmo" | null). Lifted out of BraintreeCheckout (U2) so the
  // card-step email slot is passed down only when card is the selected method
  // (and so the card-path charge identity can gate on it).
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<
    string | null
  >(null);

  // The variant's typed email is the charge identity ONLY on the card path.
  // `watchedEmail` is a shared react-hook-form field that stays populated for a
  // wallet buyer too — via the `?email=` deep-link prefill, or a card→wallet
  // method switch (RHF doesn't unregister the field) — so gating only on
  // `variantEmailValid` would feed that stale card/deep-link email into the
  // charge and make BraintreeCheckout skip the wallet-payload email (U3) + the
  // U4 fallback (its `!chargeRegisterUser` branch). Gate on the active method
  // being card so wallet buyers resolve their email from the authorization
  // payload instead (review: correctness + adversarial + frontend-races).
  const variantCardEmail =
    variantNewUser && selectedPaymentOption === "card" && variantEmailValid
      ? watchedEmail
      : undefined;

  // Charge email source. A new variant CARD user's email comes from the live
  // (validated) form value; everyone else falls back to the
  // authenticated/upgrade/existing-account sources. Gift reads the live form
  // value (getValues) because its email is on the single screen with no Step-1
  // snapshot (OMG-1963). Mirrors the resolvedEmail guard in
  // BraintreeCheckout.onSubmit. A classic existing-account buyer's step-1
  // email outranks a bare `?email=` param: step 1 is not prefilled from the
  // URL, so the email they just confirmed there (including after the
  // OMG-2693 CUSTOMER_EXISTS switch) is the account the purchase must go to.
  // Upgrade deep links never submit step 1, so they still use the URL email.
  const resolvedUpgradeEmail = variantNewUser
    ? variantCardEmail
    : authenticatedEmail ||
      (!isGift && formMode === "existing" ? formData?.email : undefined) ||
      searchParams.get("email") ||
      (isGift ? getValues("email") : undefined);

  // Charge credentials (extracted per review — mirrors resolvedUpgradeEmail).
  // Variant new user: supplied only on the card path (see variantCardEmail).
  // On the wallet path this is undefined, so BraintreeCheckout builds
  // registerUser from the payload email + variantDummyPassword (U3), or drops
  // to the U4 email-entry fallback. Control: the step-1 credentials, only in
  // create-account mode. The password field unmounts in existing-account
  // mode, but react-hook-form keeps an unmounted field's value, so a password
  // typed before a switch to "existing" would otherwise still ride the charge
  // and the BE would read it as new-account intent (CUSTOMER_EXISTS again,
  // looping the OMG-2693 switch).
  const resolvedRegisterUser = variantNewUser
    ? variantCardEmail
      ? { email: variantCardEmail, password: variantDummyPassword }
      : undefined
    : formMode === "new" && formData?.password
      ? { email: formData.email, password: formData.password }
      : undefined;

  useEffect(() => {
    // Never leave the payment-first screen gated forever if the auth check
    // stalls (e.g. a hung token refresh): flip the gate after a bound so the
    // buyer is never stranded on a blank screen, mirroring the never-strand
    // contract of the pricing path (R17). The window is generous so a normal
    // (cached or quickly-refreshed) auth check always wins and there is no
    // flash; only a pathological hang hits it, and then the worst case is the
    // pre-fix behavior (the screen renders and self-corrects once auth
    // resolves) rather than a permanent blank (review: frontend-races).
    const authGateFallback = setTimeout(() => setAuthResolved(true), 4000);
    Auth.currentAuthenticatedUser()
      .then((result) => {
        if (!result.success) return;
        setIsAuthenticated(true);
        setAuthenticatedEmail(result.data.authenticatedUser.email);
        setFormMode("existing");
        setValue("email", result.data.authenticatedUser.email);
      })
      // Settled (authenticated or not, or even a rejection) — release the
      // payment-first layout gate. Runs in every case, so a hang is the only
      // way to reach the timeout above (OMG-2145).
      .finally(() => {
        clearTimeout(authGateFallback);
        setAuthResolved(true);
      });
    return () => clearTimeout(authGateFallback);
  }, [setValue]);

  // Variant parity: a bare `?email=` deep link (no isUpgrade) pre-fills the
  // card-step email field, matching control where that param already feeds the
  // charge via the upgradeEmail fallback. Mount-only — the URL param is read
  // once; the authenticated-user effect above overrides it if it resolves.
  useEffect(() => {
    if (variantNewUser && upgradeEmail) {
      setValue("email", upgradeEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUpgrade) {
      setSignUpComplete(true);
    }
  }, [isUpgrade]);
  const { t, i18n } = useTranslation();
  const { analytics } = useAnalytics();
  // useAnalytics() returns a fresh object every render (it rebuilds the literal
  // + closures each call), so it must NOT sit in effect dep arrays — doing so
  // re-runs the effects on every render, which cancelled the in-flight probe
  // and perpetually reset the Turnstile-hung timer (code-review findings). Read
  // the latest analytics through a ref instead, and give the effects stable
  // deps. track() picks up the current experiment/arm globals either way.
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;

  // OMG-2313 card-form micro-funnel, payment-first email leg. On this arm the
  // card step owns the email field (control collects it at Step 1), so these two
  // complete the per-field picture the Drop-in's own events give us for the card
  // fields — including whether buyers who fill the bottom email field ever
  // finish the card fields above it. Single-fire per checkout session via refs:
  // focus repeats, and variantEmailValid is recomputed on every keystroke.
  const cardEmailEngagedRef = useRef(false);
  const cardEmailValidRef = useRef(false);
  const cardEmailActive = variantNewUser && selectedPaymentOption === "card";
  const trackCardEmailEngaged = () => {
    if (cardEmailEngagedRef.current) return;
    cardEmailEngagedRef.current = true;
    analyticsRef.current.track({
      event: AnalyticsEvent.CHECKOUT_CARD_EMAIL_ENGAGED,
      props: { ...CHECKOUT_SOURCE, ...deviceContext() },
    });
  };
  useEffect(() => {
    if (!cardEmailActive || !variantEmailValid) return;
    if (cardEmailValidRef.current) return;
    cardEmailValidRef.current = true;
    analyticsRef.current.track({
      event: AnalyticsEvent.CHECKOUT_CARD_EMAIL_VALID,
      props: { ...CHECKOUT_SOURCE, ...deviceContext() },
    });
  }, [cardEmailActive, variantEmailValid]);

  // OMG-2134: on checkout mount, probe whether the buyer can even reach the
  // omg-api host BEFORE card entry. Fires checkout_api_unreachable when the
  // host never answered — catching the OMG-2065 silent-spinner class up front.
  // The measurement drove the move off the black-holed onrender.com host to
  // api.omgyes.com (OMG-2066/OMG-2382/OMG-2539). Mount-once; a banner is
  // intentionally deferred: more than a quarter of flagged buyers still
  // purchase within the day (OMG-2382 read: 28%, against a 46% baseline), so a
  // "you cannot check out" banner would be wrong for every one of them, and
  // would talk the rest out of an order some of them would have completed.
  // The AbortController makes an unmount mid-probe a
  // `cancelled` outcome, which is not a verdict and is not tracked (OMG-2589).
  useEffect(() => {
    const controller = new AbortController();
    void probeCheckoutApi(controller.signal).then((result) => {
      if (result.reachable || result.outcome === "cancelled") return;
      analyticsRef.current.track({
        event: AnalyticsEvent.CHECKOUT_API_UNREACHABLE,
        props: {
          ...CHECKOUT_SOURCE,
          host: result.host,
          outcome: result.outcome,
          error_code: result.code ?? "unknown",
          attempts: result.attempts,
          elapsed_ms: result.elapsedMs,
        },
      });
    });
    return () => controller.abort();
  }, []);

  // OMG-2134: invisible Turnstile normally mints a token within a second or
  // two. When it's required (logged-out buyer) and no token has arrived after a
  // clearly-abnormal wait, fire human_verification_hung once — making the
  // "Turnstile Widget seem to have hung" case (NMH-REACT-CYJ) a funnel-joinable
  // event instead of only a console log. Only the FIRST (initial-load) hang is
  // reported; a later expiry re-arm is suppressed by the ref.
  const turnstileHungFiredRef = useRef(false);
  // Set once Turnstile has ever minted a token. Only a genuine initial-load
  // hang (no first token within the window) should arm/fire this event; a
  // later expiry re-challenge is already covered by human_verification_expired
  // and must not re-fire here (onExpire clears turnstileToken, which would
  // otherwise re-arm the timer).
  const turnstileEverMintedRef = useRef(false);
  useEffect(() => {
    if (
      !turnstileRequired ||
      !turnstileContainer ||
      turnstileInteracting ||
      turnstileToken ||
      turnstileHungFiredRef.current ||
      turnstileEverMintedRef.current
    )
      return;
    const timer = setTimeout(() => {
      if (turnstileHungFiredRef.current || turnstileEverMintedRef.current)
        return;
      turnstileHungFiredRef.current = true;
      analyticsRef.current.track({
        event: AnalyticsEvent.HUMAN_VERIFICATION_HUNG,
        props: { ...CHECKOUT_SOURCE },
      });
    }, TURNSTILE_HUNG_MS);
    return () => clearTimeout(timer);
  }, [
    turnstileRequired,
    turnstileContainer,
    turnstileInteracting,
    turnstileToken,
  ]);

  const giftFields: GiftFields | undefined = isGift
    ? {
        isGift: true,
        giftDeliveryType:
          (searchParams.get("giftDeliveryType") as "send" | "print") || "send",
        giftRecipientEmail: searchParams.get("giftRecipientEmail") || undefined,
        giftMessage: searchParams.get("giftMessage") || undefined,
        giftDeliveryDate: searchParams.get("giftDeliveryDate") || undefined,
        senderName: searchParams.get("senderName") || undefined,
        giftCardImage: searchParams.get("giftCardImage") || undefined,
      }
    : undefined;

  const rawOption = searchParams.get("option") ?? "";
  // Shared normalizer (checkoutPrefetch.ts) — the pre-mount prefetch warms
  // the post-redirect "series" key from the same function, so the validity
  // rule and the invalid-option default can't drift between the two.
  const { isValid: isValidOption } = resolveCheckoutOption(rawOption);

  if (!isValidOption) {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("option", "series");
    setSearchParams(newParams, { replace: true });
  }

  const [cookies] = useCookies(["discount_source"]);
  const discountCode =
    searchParams.get("discount_source") || cookies.discount_source || null;

  const {
    data: productPricing,
    isError: isPricingError,
    error: pricingError,
    refetch: refetchPricing,
  } = useQuery(
    getProductPricingQueryOptions({
      enabled: isValidOption,
      option: rawOption as PurchaseOption,
    }),
  );

  // If the pricing query permanently fails (Render outage, omg-api bug,
  // network down past React Query's retry budget), `productPricing`
  // stays undefined forever — which means the Step 1 Continue button
  // stays `disabled={!productPricing}` forever and the user has no idea
  // what's wrong. Surface the error to the user (see retry UI below)
  // AND capture to Sentry so we don't lose visibility on a downed
  // pricing endpoint in prod. Fires once per error-state transition.
  useEffect(() => {
    if (isPricingError) {
      captureException(pricingError ?? new Error("Pricing query failed"), {
        tags: { area: "checkout", phase: "pricing-fetch" },
        extra: { option: rawOption },
      });
    }
  }, [isPricingError, pricingError, rawOption]);

  const { data: discountInfo } = useQuery(
    getDiscountQueryOptions(discountCode),
  );

  // No card-email focus management (OMG-2102): the email moved to the BOTTOM
  // of the card form (under Postal Code, via cardEmailSlot below), so the
  // Drop-in's own card-number autofocus now lands on the form's natural first
  // field and the OMG-2097 focus overrides were retired with the layout.

  useEffect(() => {
    analytics.page({ page: "checkout_page" });
  }, []);

  // Fire the GA4 ecommerce entry sequence on mount once the URL option is
  // valid. Legacy doSignup fired this synchronously on server render
  // (~50ms post-nav); gating it on the productPricing useQuery introduced
  // a ~1.5s delay (longer on Render cold starts) where bouncing users
  // were never counted in the funnel. Fire ASAP with whatever is
  // available; price and currency are best-effort, but the funnel-step
  // events are not.
  useEffect(() => {
    if (beginCheckoutFiredRef.current || !isValidOption) return;
    beginCheckoutFiredRef.current = true;
    // Skip when the early-fire in main.tsx already pushed
    // add_to_cart/select_item/begin_checkout. The early fire runs
    // before React mounts so the legacy /doSignup funnel timing is
    // matched within ~500ms-2s of navigation instead of 2-6s here.
    // The ref above is still set first so any future invocation of
    // this effect (e.g. on prop changes) doesn't re-fire either.
    if (window.__omgyesEarlyCheckoutFired) return;
    const amount = activePricing?.amount ?? productPricing?.amount;
    const currency =
      activePricing?.currency ?? productPricing?.currency ?? "USD";
    const value =
      amount !== undefined && discountInfo?.discountPercent
        ? Math.round(amount - amount * (discountInfo.discountPercent / 100))
        : amount;
    // GA4 ecommerce items array, matching the legacy doSignup schema so
    // Convert's ga_import goals and downstream Ads conversions see the
    // same shape regardless of which checkout the user landed on.
    const ecommerceProps = {
      ...CHECKOUT_SOURCE,
      currency,
      value,
      items: [
        {
          item_id: rawOption,
          item_name: productPricing?.name ?? rawOption,
          coupon: discountCode ?? "",
          discount: discountInfo?.discountPercent ?? "",
          index: 0,
          price: amount,
          quantity: 1,
        },
      ],
    };
    // Mirror the legacy doSignup ordering (add_to_cart → select_item →
    // begin_checkout) so GA4 ecommerce funnels and any Mixpanel reports
    // keyed on this sequence stay consistent across both variants.
    analytics.track({
      event: AnalyticsEvent.ADD_TO_CART,
      props: ecommerceProps,
    });
    analytics.track({
      event: AnalyticsEvent.SELECT_ITEM,
      props: ecommerceProps,
    });
    analytics.track({
      event: AnalyticsEvent.BEGIN_CHECKOUT,
      props: ecommerceProps,
    });
  }, [isValidOption]);

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      try {
        setFormData(data);
        setSignUpComplete(true);
        setModeSwitchNotice(null);
        // Match legacy: sign_up fires only when a new (non-gift) account is
        // created at checkout, not for existing-user or gift flows.
        if (formMode === "new" && !isGift) {
          analytics.track({
            event: AnalyticsEvent.SIGN_UP,
            props: { ...CHECKOUT_SOURCE, email: data.email },
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    [analytics, formMode, isGift],
  );

  // Gift checkout is a single screen (email field above the payment form, no
  // "Continue" step — matches prod). The payment section is shown immediately
  // for gifts; for regular checkout it stays gated behind signUpComplete.
  const showPayment = isGift || signUpComplete;

  // Gift single-screen has no Continue button, so the email is validated at
  // Buy Now time. This mirrors the Continue path exactly (same validation +
  // error population via handleSubmit) but resolves a promise instead of
  // advancing a step, and stashes formData so handleSuccess / GiftConfirmation
  // (which read formData?.email) keep working. Returns false to abort the charge.
  const validateGiftEmail = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        handleSubmit(
          (data) => {
            setFormData(data);
            resolve(true);
          },
          (validationErrors) => {
            setErrors(validationErrors);
            document.getElementById("gift-email-input")?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            document.getElementById("gift-email-input")?.focus();
            resolve(false);
          },
        )();
      }),
    [handleSubmit],
  );

  const isExploreBundleOption =
    productPricing?.name === "series,series2,series3";

  // The fiftyOff hero copy is structured for a column layout (each span
  // is its own line). seriesBoth is a single sentence and breaks
  // visually under flex-col, so only apply the column layout when
  // actually rendering fiftyOff.
  const useFiftyOffKey =
    i18n.exists("checkoutPage.subtitle.fiftyOff", { fallbackLng: [] }) &&
    isExploreBundleOption &&
    !discountInfo;

  const handleChangeFormMode: MouseEventHandler = (event) => {
    event.preventDefault();
    setErrors({});
    // A manual mode change is not a bounce: without this, the notice from an
    // earlier bounce would resurface when the buyer toggles to "I already
    // have an account" and back.
    setModeSwitchNotice(null);
    setFormMode(formMode === "new" ? "existing" : "new");
  };

  const handleSuccess = async ({
    transactionId,
    signInFailed,
    email: resolvedChargeEmail,
    idempotencyKey,
    elapsedMs,
  }: {
    transactionId: string;
    signInFailed?: boolean;
    // The email the charge/account was created with. For card buyers this
    // equals the typed `watchedEmail`; for wallet/fallback buyers it's the
    // payload/entered email that only BraintreeCheckout saw.
    email?: string;
    // OMG-2134: the attempt's idempotency key + charge round-trip duration, so
    // the success settle event joins its checkout_submitted 1:1 on
    // idempotency_key (making "submitted with no result" queryable).
    idempotencyKey?: string;
    elapsedMs?: number;
  }) => {
    // Idempotent: a repeat success call must not re-fire purchase analytics,
    // the Convert revenue push, or re-route.
    if (purchaseTrackedRef.current) return;
    purchaseTrackedRef.current = true;

    // Replicate the legacy showConfirmation tracking from
    // omgyes-server checkoutService.js: client_side_purchase + the
    // USD-converted convert_purchase_usd. Both feed downstream ad
    // platforms and Convert.com that key off these specific event names
    // — keep the field shape identical to avoid breaking those
    // integrations.
    const price = discountedAmount ?? effectiveAmount ?? productPricing?.amount;
    const currency = effectiveCurrency ?? productPricing?.currency ?? "USD";
    const option = productPricing?.name;
    // From the SKU, like omg-api, not from the `isUpgrade` query param: that
    // also rides full-bundle links for members who own nothing (OMG-2717).
    const purchaseIsUpgrade = isUpgradeOption(option);
    const email =
      authenticatedEmail ??
      formData?.email ??
      upgradeEmail ??
      (variantNewUser ? (resolvedChargeEmail ?? watchedEmail) : undefined);

    // U9 sign_up parity: control fires sign_up at the Step-1 account form
    // (onSubmit), but the multi-step variant has no Step-1 — the account is
    // created at charge — so fire it here for new (non-gift) variant buyers.
    // variantNewUser already excludes gift + authenticated/upgrade, matching
    // the legacy "new account created at checkout" semantics, and the
    // purchaseTrackedRef guard above keeps it single-fire. Carries the same
    // email field the control sign_up does (no extra PII — Segment posture).
    if (variantNewUser) {
      analytics.track({
        event: AnalyticsEvent.SIGN_UP,
        props: { ...CHECKOUT_SOURCE, email },
      });
    }
    // getSegmentUser only enriches the purchase events with the Segment ids —
    // its failure must never lose the sale's tracking or wedge the paid user on
    // the checkout screen (the idempotency guard above is already latched, so a
    // throw here would block a retry too). Degrade to empty ids and proceed.
    let userId = "";
    let anonId: string | undefined;
    try {
      // Bound the Segment lookup: a wedged SDK returns a promise that NEVER
      // settles, which try/catch cannot rescue — that stranded paid users on the
      // spinner after a successful charge (OMG-2147). onSubmit's copy was bounded
      // in #873 but this post-charge copy was missed. Degrade to empty ids.
      const segmentUser = await withTimeout(
        analytics.getSegmentUser(),
        SEGMENT_USER_TIMEOUT_MS,
        "getSegmentUser",
      );
      userId = segmentUser.id() ?? "";
      anonId = segmentUser.anonymousId() ?? undefined;
    } catch (error) {
      // ids unavailable — purchase events + routing still fire below. A
      // TimeoutError means the SDK wedged (the OMG-2147 class); capture it for
      // visibility while still degrading so the paid user always proceeds.
      if (error instanceof TimeoutError) {
        captureException(error, {
          tags: { area: "checkout", phase: "post-charge-segment-user-timeout" },
        });
      }
    }

    analytics.track({
      event: AnalyticsEvent.CLIENT_SIDE_PURCHASE,
      props: {
        ...CHECKOUT_SOURCE,
        email,
        transactionId,
        orderId: transactionId,
        price,
        value: price,
        currency,
        option,
        discountCode,
        isUpgrade: purchaseIsUpgrade,
        userId,
        anonId,
        // OMG-2134: join key back to checkout_submitted + charge round-trip ms.
        idempotency_key: idempotencyKey,
        elapsed_ms: elapsedMs,
      },
    });

    const usd =
      price !== undefined
        ? convertRevenueUsd(currency, price, productPricing)
        : null;
    const convertedPrice = usd ?? price;
    const convertedCurrency = usd !== null ? "USD" : currency;

    analytics.track({
      event: AnalyticsEvent.CONVERT_PURCHASE_USD,
      props: {
        ...CHECKOUT_SOURCE,
        email,
        transactionId,
        orderId: transactionId,
        price: convertedPrice,
        value: convertedPrice,
        currency: convertedCurrency,
        option,
        discountCode,
        isUpgrade: purchaseIsUpgrade,
        userId,
        anonId,
        isGift,
      },
    });

    // Manual revenue push to Convert.com so experiments can attribute
    // this purchase. Mirrors the legacy showConfirmation block; goal
    // ID 100422695 is the same one configured in production.
    window._conv_q = window._conv_q || [];
    window._conv_q.push(["pushRevenue", convertedPrice, "1", "100422695"]);

    if (isGift) {
      return setPurchaseComplete(true);
    }

    // Payment-first new users set a real password before leaving checkout.
    // Render it inline (see the accountSetup branch in the component body) so
    // the in-memory dummy stays in this component tree and the post-charge
    // session is still live for updatePassword.
    if (variantNewUser) {
      return setAccountSetup({
        signInFailed: signInFailed ?? false,
        email: resolvedChargeEmail ?? watchedEmail,
      });
    }

    navigate(`/post-checkout/${productPricing?.slug}`);
  };

  // Effective price reflects any PayPal-driven currency swap (see activePricing).
  const effectiveAmount = activePricing?.amount ?? productPricing?.amount;
  const effectiveCurrency = activePricing?.currency ?? productPricing?.currency;

  const discountedAmount =
    effectiveAmount !== undefined && discountInfo?.discountPercent
      ? Math.round(
          effectiveAmount -
            effectiveAmount * (discountInfo.discountPercent / 100),
        )
      : undefined;

  if (isGift && purchaseComplete) {
    return (
      <GiftConfirmation
        deliveryType={giftFields?.giftDeliveryType || "send"}
        recipientEmail={giftFields?.giftRecipientEmail}
        purchaserEmail={formData?.email}
        deliveryDate={giftFields?.giftDeliveryDate}
      />
    );
  }

  if (accountSetup) {
    // Charge succeeded but post-charge sign-in failed (U3): there is no
    // session for updatePassword. Confirm the purchase and route the paid user
    // to account recovery rather than a password form that cannot succeed.
    if (accountSetup.signInFailed) {
      return (
        <div className="w-full max-w-[500px] mx-auto py-[40px] text-center">
          <h1 className="font-mabry-pro-bold text-32px tracking-tight">
            {t("setPassword.recoveryTitle", {
              defaultValue: "Your purchase is complete",
            })}
          </h1>
          <p className="mt-[8px] mb-[24px] text-16px text-ui-coal">
            {t("setPassword.recoveryBody", {
              defaultValue:
                "We couldn't finish signing you in. Use “Forgot password” to set your password and access your account.",
            })}
          </p>
          <a
            className="link text-cta underline"
            href="/forgot-password"
            onClick={(e) => {
              e.preventDefault();
              navigate("/forgot-password", {
                state: { email: accountSetup.email },
              });
            }}
          >
            {t("setPassword.recoveryCta", {
              defaultValue: "Set your password",
            })}
          </a>
        </div>
      );
    }
    return (
      <VariantSetPassword
        email={accountSetup.email}
        dummyPassword={variantDummyPassword}
        onComplete={async () => {
          analytics.track({
            event: AnalyticsEvent.ACCOUNT_FORM_COMPLETED,
            props: { ...CHECKOUT_SOURCE },
          });
          navigate(`/post-checkout/${productPricing?.slug}`);
        }}
      />
    );
  }

  // OMG-2173: the interaction-only challenge, handed to BraintreeCheckout as a
  // slot so it renders below the TOS/assent and directly above the Buy Now (per
  // product Figma), on whichever CTA block is active (card / wallet-fallback /
  // express). Clean traffic still sees a zero-height slot; a distrusted IP gets
  // Cloudflare's one-click checkbox right above the button. Undefined for authed
  // buyers — server-side exempt, so no widget and no gate (turnstileRequired is
  // authResolved && !isAuthenticated).
  const turnstileSlot = turnstileRequired ? (
    <div ref={trackTurnstileMount} className="w-full max-w-[500px]">
      {/* OMG-2228: 8px (was 16px) to reclaim vertical space in the challenged
          state so the TOS + checkbox + Buy Now trio stays above the fold on the
          smallest screens. Only renders when challenged, so clean traffic and
          the A/B control baseline are unaffected. */}
      <div className="mt-[8px]">
        <Turnstile
          className="mx-auto"
          key="turnstile-widget"
          ref={turnstileRef}
          siteKey={import.meta.env.VITE_CLOUDFLARE_SITEKEY!}
          options={{
            size: "flexible",
            appearance: "interaction-only",
            theme: "light",
          }}
          scriptOptions={{
            // Primary, precise signal that the widget script is dead — this is
            // the <script> tag's own onerror, so it fires on a genuine load
            // failure (blocked host / CSP / proxy / Cloudflare outage) and
            // NEVER while the script is merely slow. That's the actual error
            // that warrants the hint, so surface it here instead of guessing
            // from a timer. Validated locally: DevTools domain-block and
            // content-blocker rules both trip this.
            onError: () => {
              // A real load failure counts as the widget having "responded":
              // it short-circuits the last-resort timer (which only fires when
              // NOTHING happened), so the hint isn't redundantly re-asserted.
              turnstileRespondedRef.current = true;
              turnstileBlockedSourceRef.current = "script_error";
              setTurnstileBlocked(true);
              analytics.track({
                event: AnalyticsEvent.HUMAN_VERIFICATION_FAILURE,
                props: { ...CHECKOUT_SOURCE, error: "script_load_error" },
              });
            },
          }}
          onWidgetLoad={() => {
            // The widget script loaded and rendered (incl. the interaction-only
            // checkbox that waits for a click) — the true "script is alive"
            // signal that flips the load-timeout backstop off.
            turnstileRespondedRef.current = true;
            // Retract ONLY a timeout-sourced false positive: a slow-but-alive
            // script whose render lost the race with the backstop timer. A
            // genuine 'challenge'/'script_error'/'unsupported' block must
            // survive — never erase a real failure into a gated Buy Now with no
            // message. (Guards against a future library that re-fires this.)
            if (turnstileBlockedSourceRef.current === "timeout") {
              turnstileBlockedSourceRef.current = null;
              setTurnstileBlocked(false);
            }
          }}
          onBeforeInteractive={() => {
            // A visible challenge is waiting for the buyer, not a hung widget.
            turnstileRespondedRef.current = true;
            setTurnstileInteracting(true);
          }}
          onAfterInteractive={() => {
            setTurnstileInteracting(false);
          }}
          onSuccess={(token) => {
            turnstileRespondedRef.current = true;
            turnstileRetriedRef.current = false;
            turnstileBlockedSourceRef.current = null;
            setTurnstileBlocked(false);
            setTurnstileToken(token);
            setTurnstileInteracting(false);
            // OMG-2134: a token has been minted, so a later expiry re-challenge
            // must not re-arm the initial-load hung timer (that path is covered
            // by human_verification_expired).
            turnstileEverMintedRef.current = true;
            analytics.track({
              event: AnalyticsEvent.HUMAN_VERIFICATION_SUCCESS,
              props: { ...CHECKOUT_SOURCE },
            });
          }}
          onError={(error) => {
            turnstileRespondedRef.current = true;
            analytics.track({
              event: AnalyticsEvent.HUMAN_VERIFICATION_FAILURE,
              props: { ...CHECKOUT_SOURCE, error: String(error ?? "") },
            });
            // Retry once — many Turnstile errors are transient.
            if (!turnstileRetriedRef.current) {
              turnstileRetriedRef.current = true;
              turnstileRef.current?.reset();
              return;
            }
            // Second consecutive failure: no token is coming. Clear any stale
            // token and surface the actionable hint below. Marked 'challenge'
            // so a later onWidgetLoad can't retract this real failure.
            setTurnstileToken(undefined);
            turnstileBlockedSourceRef.current = "challenge";
            setTurnstileBlocked(true);
          }}
          onExpire={() => {
            turnstileRespondedRef.current = true;
            // Token lifetime elapsed before submit. Drop it and let the widget
            // re-run; treat the retry as a clean slate (clear the blocked hint
            // too, so a fresh challenge isn't shown next to a stale message).
            turnstileRetriedRef.current = false;
            turnstileBlockedSourceRef.current = null;
            setTurnstileBlocked(false);
            setTurnstileToken(undefined);
            analytics.track({
              event: AnalyticsEvent.HUMAN_VERIFICATION_EXPIRED,
              props: { ...CHECKOUT_SOURCE },
            });
          }}
          onUnsupported={() => {
            turnstileRespondedRef.current = true;
            turnstileBlockedSourceRef.current = "unsupported";
            setTurnstileBlocked(true);
            analytics.track({
              event: AnalyticsEvent.HUMAN_VERIFICATION_UNSUPPORTED,
              props: { ...CHECKOUT_SOURCE },
            });
          }}
        />
      </div>
      {turnstileBlocked && (
        <p
          role="alert"
          className="mb-[16px] text-center font-mabry-pro-medium text-[14px] text-ui-pepper"
        >
          {t("js.checkout.turnstileBlocked", {
            defaultValue:
              "We're having trouble verifying your connection. If you're using a VPN, disabling it may help.",
          })}
        </p>
      )}
    </div>
  ) : undefined;

  return (
    <>
      <div className="flex flex-col items-center min-h-screen bg-white">
        {/* Header. The border-b lives on the outer full-width wrapper
            so the divider stretches edge-to-edge, matching the legacy
            doSignup header and the skeleton's cko-sk-header. The inner
            max-w container holds the actual logo/login layout. */}
        <div className="w-full border-ui-sea-salt border-b">
          <div className="sm:h-[71px] flex flex-row justify-between w-full max-w-[1024px] xl:max-w-[1200px] min-h-[68px] px-[18px] lg:px-[32px] xl:px-0 xl:mx-auto">
            <div
              className="w-[94px] sm:w-[118px] my-[22px] h-16px sm:h-20px hover:cursor-pointer"
              onClick={() =>
                (window.location.href = import.meta.env.VITE_OMGYES_SERVER_URL)
              }
            >
              {blackOmgyes}
            </div>
            {!isGift && (
              <Button
                className="text-[#323232] font-mabry-pro-medium p-0 my-[22px] leading-[16px]"
                variant="link"
                onClick={() => navigate("/login")}
              >
                Login
              </Button>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="w-full max-w-[1040px] min-h-screen">
          {/* Hero banner — served as <picture> with WebP + JPEG fallbacks
              and a mobile-sized variant. Modern iPhone (iOS Safari 14+,
              ~99% of iOS traffic) gets an 11 KB mobile WebP; modern desktop
              gets a 28 KB desktop WebP. Old Safari falls through to the
              41 KB mobile JPEG or original 80 KB JPEG. The 768px breakpoint
              matches the mobile/desktop split used by the inline preload
              link in index.html. */}
          <div className="relative overflow-hidden h-[132px] sm:h-[200px] lg:rounded-lg lg:mx-32px xl:mx-0">
            <picture>
              <source
                type="image/webp"
                media="(max-width: 767.98px)"
                srcSet="/members/assets/checkout/checkout-banner-mobile.webp"
              />
              <source
                type="image/webp"
                srcSet="/members/assets/checkout/checkout-banner.webp"
              />
              <source
                media="(max-width: 767.98px)"
                srcSet="/members/assets/checkout/checkout-banner-mobile.jpg"
              />
              <img
                src="/members/assets/checkout/checkout-banner.jpg"
                alt=""
                role="presentation"
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover object-[63%_71%] sm:object-[70%_75%] md:object-[54%_79%]"
              />
            </picture>
            <div className="relative h-full flex items-center pl-[16px] sm:pl-[48px]">
              <p
                className={clsx(
                  "font-mabry-pro-regular text-white font-bold text-[20px] sm:text-[26px] leading-[28px] sm:leading-[30px] sm max-w-[270px] sm:max-w-[400px] lg:max-w-[500px] uppercase md:text-[26px] md:leading-[30px] md:max-w-[400px]",
                  useFiftyOffKey && !isGift && "flex flex-col",
                )}
              >
                {isGift ? (
                  t("checkoutPage.subtitle.gift")
                ) : (
                  <Trans
                    i18nKey={
                      useFiftyOffKey
                        ? "checkoutPage.subtitle.fiftyOff"
                        : "checkoutPage.subtitle.seriesBoth"
                    }
                    components={{ span: <span /> }}
                  />
                )}
              </p>
            </div>
          </div>

          {/* Step 1 (account form: email/password) renders unconditionally —
              it doesn't reference `productPricing` and was hidden behind a
              CircleLoader during the pricing API call (~300-500ms),
              causing the inline HTML skeleton's form to briefly disappear
              and a spinner to flash in its place before the React form
              re-rendered. Step 2 (payment) still depends on pricing data
              and is gated below. */}
          <div className="mx-auto">
            {/* Account form (step 1) — regular non-variant checkout only.
                The payment-first variant collapses this into the single
                payment screen below (no password, no Continue gate); gift
                checkout is also a single screen with its email above the
                payment form (OMG-1963). Both skip this step. */}
            {!paymentFirstActive && !isGift && !signUpComplete && (
              <>
                <div className="sm:mt-[18px] p-[20px] lg:pt-0 sm:px-0 pb-16px">
                  {!isGift && (
                    <>
                      <p className="leading-[26px] font-mabry-pro-bold text-[18px] text-center text-ui-pepper">
                        {formMode === "new"
                          ? t("checkoutPage.create")
                          : t("checkoutPage.existing")}
                      </p>
                      <div className="text-center mt-[4px]">
                        <Link
                          className="text-cta hover:text-cta-hover text-[14px] font-mabry-pro-regular underline leading-[24px]"
                          onClick={handleChangeFormMode}
                          to=""
                        >
                          {formMode === "new"
                            ? t("checkoutPage.switchToExisting")
                            : t("checkoutPage.switchToCreate")}
                        </Link>
                      </div>
                    </>
                  )}
                  {isGift && (
                    <p className="font-mabry-pro-bold text-[18px] text-center text-ui-pepper">
                      {t("checkoutPage.confirmationEmail")}
                    </p>
                  )}
                  {modeSwitchNotice === "emailNotRecognized" &&
                    !isGift &&
                    formMode === "new" && (
                      <p
                        role="alert"
                        className="max-w-[500px] mx-auto mt-[16px] text-center font-mabry-pro-medium text-[14px] text-ui-pepper"
                      >
                        {t("js.checkout.createNewAccount", {
                          defaultValue:
                            "It seems we don't recognize your email. Try creating a new account.",
                        })}
                      </p>
                    )}
                  {modeSwitchNotice === "accountExists" &&
                    !isGift &&
                    !isAuthenticated &&
                    formMode === "existing" && (
                      <p
                        role="alert"
                        className="max-w-[500px] mx-auto mt-[16px] text-center font-mabry-pro-medium text-[14px] text-ui-pepper"
                      >
                        {t("js.checkout.accountExistsContinue", {
                          defaultValue:
                            "An account with this email already exists. Continue to add this purchase to it.",
                        })}
                      </p>
                    )}
                  <Form
                    className="max-w-[500px] mt-[20px] sm:mt-[32px] flex flex-col mx-auto"
                    onSubmit={handleSubmit(onSubmit, setErrors)}
                    autoComplete="on"
                  >
                    <FormInput
                      inputClassName={CHECKOUT_INPUT_CLASS}
                      labelClassName="tracking-normal cursor-pointer"
                      errorClassName="hidden"
                      inputErrorClassName="!border !border-solid !border-error focus:!border-error"
                      showErrorIcon
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                      required
                      className="w-full flex flex-col gap-[4px]"
                      register={() =>
                        register(
                          "email",
                          emailValidationRules(t("js.checkout.emailInvalid")),
                        )
                      }
                      label={
                        isGift
                          ? t("checkoutPage.emailGift")
                          : t("checkoutPage.email")
                      }
                      error={errors.email}
                      onChange={() => setErrors({})}
                      placeholder={
                        isGift
                          ? t("checkoutPage.emailGift")
                          : t("checkoutPage.email")
                      }
                    />
                    <InputErrorLabel
                      errorMessage={errors?.email?.message as string}
                    />
                    {!isGift && formMode === "new" && (
                      <>
                        <FormInput
                          inputClassName={CHECKOUT_INPUT_CLASS}
                          labelClassName="tracking-normal cursor-pointer"
                          errorClassName="hidden"
                          inputErrorClassName="!border !border-solid !border-error focus:!border-error"
                          showErrorIcon
                          required
                          className="w-full flex flex-col gap-[4px]"
                          register={() =>
                            register("password", {
                              required: t("js.checkout.passwordInvalid"),
                              minLength: {
                                value: 6,
                                message: t("js.checkout.passwordInvalid"),
                              },
                            })
                          }
                          label={t("checkoutPage.password")}
                          error={errors.password}
                          type="password"
                          autoComplete="new-password"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          enterKeyHint="go"
                          onChange={() => setErrors({})}
                          placeholder={t("checkoutPage.password")}
                        />
                        <InputErrorLabel
                          errorMessage={errors?.password?.message as string}
                        />
                      </>
                    )}
                    {isPricingError ? (
                      // Pricing query has fully failed (after React
                      // Query's retries). Show an actionable error UI
                      // + Try again button instead of an indefinitely-
                      // disabled Continue button — without this the
                      // user has no way to recover and we have no
                      // signal (the captureException above does fire,
                      // but the user-facing experience matters too).
                      <PricingLoadError onRetry={() => refetchPricing()} />
                    ) : (
                      <div className="text-center mt-[20px] sm:mt-40px lg:mt-[20px]">
                        <Button
                          type="submit"
                          className="w-full max-w-[257px] rounded-full font-mabry-pro-bold !font-extrabold !leading-[22px] !py-12px shadow-[0_2px_0_0_rgba(41,38,37,0.05)]"
                          // Disable while pricing is still loading.
                          // The Step 1 form mounts immediately (no
                          // longer gated on productPricing) so a very
                          // fast user could fill + submit before the
                          // pricing API resolves, which would set
                          // signUpComplete=true while Step 2 is still
                          // un-mountable — leaving a brief empty
                          // middle. Matches the skeleton button's
                          // `disabled` attribute in index.html
                          // (consistent UX through the React handoff).
                          disabled={!productPricing}
                        >
                          <span className="font-mabry-pro-regular">
                            {t("label.continue")}
                          </span>
                        </Button>
                      </div>
                    )}
                  </Form>
                </div>
                <div className="px-16px text-center leading-[18px] sm:max-w-[469px] sm:mx-auto md:px-16px">
                  <span
                    className="text-center text-[12px] leading-[18px] tracking-[0.24px] font-mabry-pro-regular text-ui-coal [&_a]:underline [&_a]:text-cta"
                    dangerouslySetInnerHTML={{
                      __html: t("checkoutPage.tos"),
                    }}
                  />
                </div>
              </>
            )}

            {/* Payment section (step 2) — gated on productPricing (the
                  price display + Apple Pay/Google Pay config depend on
                  it). Step 1 above intentionally renders independently
                  so the user never sees the form disappear during the
                  brief pricing API call.

                  Within the productPricing-loaded branch, this div
                  stays mounted even before signUpComplete so the
                  BraintreeCheckout component can kick off dropin init
                  in the background — `hidden` class only hides it
                  visually. */}
            {/* R17: the payment-first variant and gift render their whole
                screen behind the pricing gate below — without this block a
                permanently failed pricing query strands them on a blank
                page (the retry UI above is control-Step-1-only). */}
            {(paymentFirstActive || isGift) &&
              isPricingError &&
              !productPricing && (
                <PricingLoadError onRetry={() => refetchPricing()} />
              )}
            {/* Payment-first waits for the auth check to settle before it
                renders — its layout depends on isAuthenticated, so rendering
                before the check resolves flashes the new-user "create account"
                UI on an authenticated/upgrade load (OMG-2145). The auth check
                overlaps the pricing fetch this block already waits on, so this
                adds no perceptible delay. Control/gift (paymentFirstActive
                false) are unaffected. */}
            {productPricing && (!paymentFirstActive || authResolved) && (
              <div
                className={clsx(
                  "flex flex-col items-center p-20px",
                  paymentFirstActive && "pf-checkout",
                  !(paymentFirstActive || showPayment) && "hidden",
                )}
              >
                {/* The method screen carries no inline login link (OMG-2098,
                    per design) — the header Login is the returning-buyer
                    path, and an R12 CUSTOMER_EXISTS rejection still catches
                    existing emails pre-charge (classic: back to step 1 in
                    existing-account mode, OMG-2693; payment-first: the
                    /login message). */}
                {discountInfo && !isGift && (
                  <p className="text-center text-[14px] sm:text-[16px] md:text-[18px] font-mabry-pro-regular text-ui-pepper pt-[6px] pb-[10px]">
                    {discountInfo.message
                      ? discountInfo.message
                          .replaceAll(
                            "{{discountPercent}}",
                            String(discountInfo.discountPercent),
                          )
                          .replaceAll(
                            "{{originalPrice}}",
                            formatPrice({
                              amount: effectiveAmount ?? productPricing.amount,
                              currency:
                                effectiveCurrency ?? productPricing.currency,
                              countryCode: productPricing.countryCode,
                            }),
                          )
                      : t("checkoutPage.discount.applied", {
                          discountPercent: discountInfo.discountPercent,
                        })}
                  </p>
                )}
                <div
                  className={clsx(
                    "flex items-center justify-center gap-[6px] text-center lg:pt-0 border-b border-ui-garlic w-full",
                    // OMG-2191: when a discount adds the promo line above, tighten
                    // the space around the gray line so Buy Now stays above the
                    // fold on small screens. No-discount layout (the A/B control
                    // baseline) is left exactly as-is.
                    discountInfo && !isGift
                      ? "pt-[6px] sm:pt-[10px] pb-[16px] mb-[16px] sm:mb-[24px]"
                      : "pt-[10px] sm:pt-[18px] pb-[30px] mb-[30px] sm:mb-[40px]",
                  )}
                >
                  <p
                    className="sm:leading-[24px] md:leading-[34px] tracking-[-0.2px] sm:flex sm:flex-col text-[16px] sm:text-[18px] md:text-[20px] font-mabry-pro-regular text-ui-pepper [&_.pv-bold-upper]:font-mabry-pro-bold [&_.pv-bold-upper]:uppercase"
                    dangerouslySetInnerHTML={{
                      __html: t(
                        lookupKeyByProduct(productPricing.name, isGift),
                        {
                          priceData: {
                            price_series_series2_series3_display: formatPrice({
                              amount:
                                discountedAmount ??
                                effectiveAmount ??
                                productPricing.amount,
                              currency:
                                effectiveCurrency ?? productPricing.currency,
                              countryCode: productPricing.countryCode,
                            }),
                            price_series_series2_series3_gift_display:
                              formatPrice({
                                amount:
                                  discountedAmount ??
                                  effectiveAmount ??
                                  productPricing.amount,
                                currency:
                                  effectiveCurrency ?? productPricing.currency,
                                countryCode: productPricing.countryCode,
                              }),
                            price_series2_series3_upgrade_display: formatPrice({
                              amount:
                                discountedAmount ??
                                effectiveAmount ??
                                productPricing.amount,
                              currency:
                                effectiveCurrency ?? productPricing.currency,
                              countryCode: productPricing.countryCode,
                            }),
                            price_series_display: formatPrice({
                              amount:
                                discountedAmount ??
                                effectiveAmount ??
                                productPricing.amount,
                              currency:
                                effectiveCurrency ?? productPricing.currency,
                              countryCode: productPricing.countryCode,
                            }),
                            price_series_gift_display: formatPrice({
                              amount:
                                discountedAmount ??
                                effectiveAmount ??
                                productPricing.amount,
                              currency:
                                effectiveCurrency ?? productPricing.currency,
                              countryCode: productPricing.countryCode,
                            }),
                          },
                        },
                      ),
                    }}
                  />
                </div>

                {/* Gift single-screen: the email field sits above the payment
                    form (OMG-1963). It reuses the shared react-hook-form
                    "email" registration; there is no Continue/submit here —
                    Buy Now validates it via validateGiftEmail. */}
                {isGift && (
                  <div className="w-full max-w-[500px] mb-[24px]">
                    <FormInput
                      id="gift-email-input"
                      inputClassName="h-[48px] border-none font-mabry-pro-regular !bg-ui-msg px-16px py-8px text-[#282828] placeholder:font-avenir-book placeholder:text-[#919191] placeholder:tracking-[0.04px] focus:!border focus:!border-solid focus:!border-black/20 focus:shadow-[0_0_10px_rgba(0,0,0,0.1)]"
                      labelClassName="tracking-normal cursor-pointer"
                      errorClassName="hidden"
                      inputErrorClassName="!border !border-solid !border-error focus:!border-error"
                      showErrorIcon
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      required
                      className="w-full flex flex-col gap-[4px]"
                      register={() =>
                        register(
                          "email",
                          emailValidationRules(t("js.checkout.emailInvalid")),
                        )
                      }
                      label={t("checkoutPage.emailGift")}
                      error={errors.email}
                      onChange={() => setErrors({})}
                      placeholder={t("checkoutPage.emailGift")}
                    />
                    <InputErrorLabel
                      errorMessage={errors?.email?.message as string}
                    />
                  </div>
                )}

                {/* Braintree payment */}
                <div className="w-full relative max-w-[500px]">
                  {/* ErrorBoundary catches a hard chunk-load failure
                      (after the retry wrapper has already given up).
                      Most commonly: user has /checkout open in a tab
                      across a deploy and their cached index.html
                      references a chunk hash that's been rotated.
                      Without this, lazy() would throw uncaught and
                      unmount the whole tree — blank /checkout for
                      that user. With it, they get a recovery UI and
                      we get a Sentry signal we can monitor. */}
                  <ErrorBoundary
                    beforeCapture={(scope) => {
                      scope.setTag("area", "checkout");
                      scope.setTag("phase", "braintree-chunk-load");
                    }}
                    fallback={({ resetError }) => (
                      <ChunkLoadError
                        onRetry={() => {
                          // The chunk memo (braintreeChunk.ts) self-reset on
                          // rejection; a rejected lazy() is cached forever,
                          // so recreate the component, then clear the
                          // boundary so the fresh lazy renders.
                          setBraintreeCheckout(() => lazy(getBraintreeChunk));
                          resetError();
                        }}
                      />
                    )}
                  >
                    <Suspense fallback={null}>
                      <BraintreeCheckout
                        productPricing={productPricing}
                        discountCode={discountCode}
                        discountPercent={discountInfo?.discountPercent}
                        turnstileToken={turnstileToken}
                        // Logged-out buyers require a token; authed buyers are
                        // server-side exempt (and get no widget). Same
                        // `turnstileRequired` (authResolved-gated) that feeds the
                        // widget render + Buy Now gate, so the #878 button gate
                        // and the #889 onSubmit token backstop share one contract.
                        turnstileRequired={turnstileRequired}
                        // OMG-2173: rendered below the TOS/assent, above the Buy
                        // Now, on whichever CTA block is active (undefined for
                        // authed buyers, so nothing renders).
                        turnstileSlot={turnstileSlot}
                        onResetTurnstile={() => {
                          // Charge consumed the single-use token. Reset ALL
                          // Turnstile state so the widget — re-mounted on a BE
                          // error (chargeSubmitted → false) — starts clean.
                          setTurnstileToken(undefined);
                          setTurnstileBlocked(false);
                          turnstileBlockedSourceRef.current = null;
                          turnstileRetriedRef.current = false;
                        }}
                        // Multi-step variant wiring (U2/U3): report the active
                        // method up so the card-step email field can render,
                        // and let the wallet path resolve + create the account
                        // from the payload email + this dummy password.
                        paymentFirstVariant={variantNewUser}
                        variantDummyPassword={variantDummyPassword}
                        onPaymentOptionChange={setSelectedPaymentOption}
                        paymentFirstActive={paymentFirstActive}
                        // Card-step email (U2/KTD2 → OMG-2102): the form's
                        // LAST field, rendered by BraintreeCheckout directly
                        // under the Drop-in's Postal Code. Wallet buyers never
                        // see it (their email comes from the authorization
                        // payload, U3); the email is confirmed after the
                        // charge on the Create-account screen (U5), so there
                        // is no confirm field here (KTD8). RHF wiring stays
                        // here — the slot closes over this form's register.
                        cardEmailSlot={
                          variantNewUser && selectedPaymentOption === "card" ? (
                            <VariantCardEmail
                              register={register}
                              variantEmailError={variantEmailError}
                              onClearErrors={() => setErrors({})}
                              onEngage={trackCardEmailEngaged}
                            />
                          ) : undefined
                        }
                        // OMG-2337 review: same live per-keystroke flag the slot
                        // above passes into VariantCardEmail. Suppresses the Buy
                        // Now email hint while the field is already showing this
                        // exact string under the input.
                        cardEmailHasLiveError={variantEmailError}
                        isUpgrade={
                          isUpgrade ||
                          (!isAuthenticated && formMode === "existing")
                        }
                        upgradeEmail={resolvedUpgradeEmail}
                        giftFields={giftFields}
                        // Gift single-screen: validate the email at Buy Now
                        // (no Continue step) and read its freshest value at
                        // submit time (getValues is non-reactive). OMG-1963.
                        onBeforeSubmit={isGift ? validateGiftEmail : undefined}
                        getLiveEmail={
                          isGift ? () => getValues("email") : undefined
                        }
                        registerUser={resolvedRegisterUser}
                        onActivePricingChange={setActivePricing}
                        onSuccess={handleSuccess}
                        onSwitchToCreateAccount={
                          // Only wire the auto-switch where step 1 can render
                          // (see the !paymentFirstActive && !isGift gate above)
                          // — on the payment-first arm the switch would be a
                          // visual no-op and the component's message fallback
                          // is the right behavior.
                          paymentFirstActive || isGift
                            ? undefined
                            : () => {
                                // BE returned EMAIL_NOT_RECOGNIZED — send the
                                // user back to step 1 in create-account mode.
                                // formData is empty on upgrade deep-links
                                // (step 1 never submitted), so fall back to
                                // the URL email.
                                const email = formData?.email ?? upgradeEmail;
                                setSignUpComplete(false);
                                setFormMode("new");
                                if (email) setValue("email", email);
                                // Say why they're back here. The switch used
                                // to be silent: a buyer who chose "I already
                                // have an account" was bounced to the
                                // create-account form with no explanation,
                                // and the BE's Sentry shows that cohort
                                // abandoning without ever creating the
                                // account. Rendered as a page-level notice
                                // above the step-1 form; cleared on the next
                                // successful step-1 submit or when the buyer
                                // switches modes manually.
                                setModeSwitchNotice("emailNotRecognized");
                              }
                        }
                        onSwitchToExistingAccount={
                          // Same wiring rule as onSwitchToCreateAccount above.
                          paymentFirstActive || isGift
                            ? undefined
                            : (email: string) => {
                                // BE returned CUSTOMER_EXISTS (OMG-2693): the
                                // email already has an account, so send the
                                // buyer back to step 1 in existing-account
                                // mode with the blocked email filled in. The
                                // next Continue + Buy Now sends no password
                                // (resolvedRegisterUser is create-mode only),
                                // so the purchase is added to that account.
                                // Landing on step 1 rather than retrying in
                                // place keeps the email in view and editable
                                // before anything is charged to it, and the
                                // buyer must press Continue and Buy Now again.
                                setSignUpComplete(false);
                                setFormMode("existing");
                                setErrors({});
                                setValue("email", email);
                                setModeSwitchNotice("accountExists");
                              }
                        }
                      />
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </div>
            )}
          </div>
        </div>
        <CheckoutFooter />
      </div>
    </>
  );
};

export default Checkout;
